import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Globe } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { visualExplainer } from "@/data/visualExplainer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
const translations = {
  de: {
    forPatients: "Für Patienten",
    research: "Studien",
    faq: "FAQs",
    register: "Registrierung",
    shop: "Shop",
    admin: "Admin",
    logout: "Abmelden",
    contact: "Kontakt zu uns",
  },
  en: {
    forPatients: "For Patients",
    research: "Research",
    faq: "FAQs",
    register: "Registration",
    shop: "Shop",
    admin: "Admin",
    logout: "Log out",
    contact: "Contact us",
  },
  fr: {
    forPatients: "Pour les patients",
    research: "Études",
    faq: "FAQs",
    register: "Inscription",
    shop: "Boutique",
    admin: "Admin",
    logout: "Déconnexion",
    contact: "Nous contacter",
  },
  nl: {
    forPatients: "Voor patiënten",
    research: "Studies",
    faq: "FAQs",
    register: "Registratie",
    shop: "Winkel",
    admin: "Admin",
    logout: "Uitloggen",
    contact: "Neem contact op",
  },
  it: {
    forPatients: "Per i pazienti",
    research: "Studi",
    faq: "FAQs",
    register: "Registrazione",
    shop: "Negozio",
    admin: "Admin",
    logout: "Disconnetti",
    contact: "Contattaci",
  },
  es: {
    forPatients: "Para pacientes",
    research: "Estudios",
    faq: "FAQs",
    register: "Registro",
    shop: "Tienda",
    admin: "Admin",
    logout: "Cerrar sesión",
    contact: "Contáctenos",
  },
};

const languageNames: Record<string, { name: string; flag: string }> = {
  de: { name: "Deutsch", flag: "🇩🇪" },
  en: { name: "English", flag: "🇬🇧" },
  fr: { name: "Français", flag: "🇫🇷" },
  nl: { name: "Nederlands", flag: "🇳🇱" },
  it: { name: "Italiano", flag: "🇮🇹" },
  es: { name: "Español", flag: "🇪🇸" },
};

export const Navigation = () => {
  const { language, setLanguage } = useLanguage();
  const t = translations[language];
  const [isOpen, setIsOpen] = useState(false);

  const menuItems = [
    { label: t.forPatients, href: "/fuer-patienten" },
    { label: visualExplainer[language].menu, href: "/eap-erklaert" },
    { label: t.research, href: "/research" },
    { label: t.faq, href: "/faq" },
    { label: t.register, href: "https://shop.eap-abutments.com/login/signup", external: true },
    { label: t.shop, href: "https://shop.eap-abutments.com", external: true },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/images/logo-eap.png" 
              alt="EAP Abutments Logo - Zur Startseite" 
              className="h-12 w-auto hover:opacity-80 transition-opacity"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden xl:flex items-center space-x-5">
            {menuItems.map((item: any) => (
              <a
                key={item.label}
                href={item.href}
                className="text-foreground hover:text-primary transition-colors duration-200"
                {...(item.external && { target: "_blank", rel: "noopener noreferrer" })}
              >
              {item.label}
            </a>
          ))}
          <Button 
            className="bg-primary hover:bg-primary-hover text-primary-foreground shadow-glow"
            onClick={() => window.location.href = '/#kontakt'}
          >
            {t.contact}
          </Button>
          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-1">
                <Globe className="w-4 h-4" />
                <span className="uppercase">{language}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {(Object.keys(languageNames) as Array<keyof typeof languageNames>).map((lang) => (
                <DropdownMenuItem
                  key={lang}
                  onClick={() => setLanguage(lang as "de" | "en" | "fr" | "nl" | "it" | "es")}
                  className={language === lang ? "bg-primary/10 text-primary" : ""}
                >
                  <span className="mr-2">{languageNames[lang].flag}</span>
                  {languageNames[lang].name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden text-foreground"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={isOpen}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="xl:hidden mt-4 pb-4 animate-fade-in">
            <div className="flex flex-col space-y-4">
              {menuItems.map((item: any) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="text-foreground hover:text-primary transition-colors duration-200 py-2"
                  onClick={() => setIsOpen(false)}
                  {...(item.external && { target: "_blank", rel: "noopener noreferrer" })}
                >
                {item.label}
              </a>
            ))}
            <Button 
              className="bg-primary hover:bg-primary-hover text-primary-foreground w-full"
              onClick={() => {
                setIsOpen(false);
                window.location.href = '/#kontakt';
              }}
            >
              {t.contact}
            </Button>
            {/* Mobile Language Selector */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
              {(Object.keys(languageNames) as Array<keyof typeof languageNames>).map((lang) => (
                <Button
                  key={lang}
                  variant={language === lang ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setLanguage(lang as "de" | "en" | "fr" | "nl" | "it" | "es");
                    setIsOpen(false);
                  }}
                  className="flex-1 min-w-[80px]"
                >
                  <span className="mr-1">{languageNames[lang].flag}</span>
                  {languageNames[lang].name}
                </Button>
              ))}
            </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};